import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, Receipt, ThumbsUp, ThumbsDown, Paperclip, DownloadSimple, X, PaperPlaneTilt, FileXls, WarningCircle } from "@phosphor-icons/react";

const CATS = ["travel", "fuel", "food", "hotel", "internet", "office_supplies", "training", "client_entertainment", "other"];
const STATUS_STYLE = {
  submitted: "bg-amber-50 text-amber-800 border-amber-200",
  draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
  approved: "bg-blue-50 text-blue-800 border-blue-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

function fmtMoney(n, cur = "INR") { return `${cur} ${Number(n || 0).toLocaleString("en-IN")}`; }
function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function AttachmentsDialog({ claim, onChanged, canWrite }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const attachments = claim.attachments || [];

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/reimbursements/${claim.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${files.length} file(s)`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const removeAtt = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/reimbursements/${claim.id}/attachments/${attId}`);
      toast.success("Removed");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/reimbursements/${claim.id}/attachments/${att.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`claim-attach-${claim.id}`}
          className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm inline-flex items-center gap-1"
        >
          <Paperclip size={12} />
          {attachments.length > 0 ? attachments.length : "Attach"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Paperclip size={16} weight="duotone" className="text-zenith-magenta" />
            Receipts &amp; invoices — {claim.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {attachments.length === 0 && (
            <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">
              No files attached yet. Add up to 10 receipts (PDF / JPG / PNG / WebP / HEIC, max 10 MB each).
            </div>
          )}
          {attachments.map((a) => (
            <div key={a.id} data-testid={`attachment-row-${a.id}`} className="flex items-center gap-2 border border-zinc-200 rounded-sm p-2">
              <div className="w-8 h-8 rounded-sm bg-zenith-teal/10 text-zenith-teal-700 grid place-items-center shrink-0">
                <Paperclip size={14} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{a.filename}</div>
                <div className="text-[11px] text-zinc-500">{a.mime} · {fmtSize(a.size)}</div>
              </div>
              <button onClick={() => download(a)} data-testid={`attachment-download-${a.id}`} className="p-1.5 rounded-sm hover:bg-zinc-100 text-zinc-600" title="Download">
                <DownloadSimple size={14} />
              </button>
              {canWrite && (
                <button onClick={() => removeAtt(a.id)} data-testid={`attachment-delete-${a.id}`} className="p-1.5 rounded-sm hover:bg-red-50 text-red-500" title="Remove">
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
        {canWrite && attachments.length < 10 && (
          <div className="pt-2 border-t border-zinc-100">
            <Label htmlFor={`att-upload-${claim.id}`} className="text-xs cursor-pointer">
              <div data-testid={`attachment-upload-${claim.id}`} className="inline-flex items-center gap-2 border border-adb-navy text-adb-navy rounded-sm px-3 py-2 hover:bg-adb-navy hover:text-white transition-colors">
                <Plus size={12} />
                {uploading ? "Uploading…" : "Upload files"}
              </div>
            </Label>
            <input id={`att-upload-${claim.id}`} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*" onChange={onUpload} className="hidden" disabled={uploading} />
            <span className="ml-2 text-[11px] text-zinc-500">
              {attachments.length}/10 files · PDF/JPG/PNG/WebP/HEIC · max 10 MB each
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Reimbursements() {
  const { user, tenantId } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const canReview = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", trip_start_date: "", trip_end_date: "", notes: "", lines: [{ date: "", category: "travel", amount: 0, currency: "INR", description: "" }] });

  const load = () => {
    if (user?.employee_id) api.get("/reimbursements/mine").then(({ data }) => setMine(data));
    if (canReview) {
      const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
      api.get("/reimbursements", { params }).then(({ data }) => setAll(data));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const total = useMemo(() => {
    const t = {};
    for (const l of form.lines) { const c = l.currency || "INR"; t[c] = (t[c] || 0) + (parseFloat(l.amount) || 0); }
    return t;
  }, [form.lines]);

  const submit = async () => {
    try {
      const payload = { ...form, lines: form.lines.map((l) => ({ ...l, amount: parseFloat(l.amount) || 0 })) };
      if (!payload.trip_start_date) delete payload.trip_start_date;
      if (!payload.trip_end_date) delete payload.trip_end_date;
      const { data } = await api.post("/reimbursements", payload);
      toast.success("Claim submitted for approval — attach your receipts now");
      setOpen(false);
      setForm({ title: "", trip_start_date: "", trip_end_date: "", notes: "", lines: [{ date: "", category: "travel", amount: 0, currency: "INR", description: "" }] });
      load();
      // Optimistic: highlight the new draft so user attaches receipts
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="claim-attach-${data.id}"]`);
        if (el) el.click();
      }, 300);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitDraft = async (id) => {
    try {
      await api.post(`/reimbursements/${id}/submit`);
      toast.success("Claim submitted for approval ✓");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/reimbursements/bulk-template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Zenith360-Bulk-Claims-Template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/reimbursements/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const errCount = data.error_count || (data.errors?.length ?? 0);
      if (errCount > 0 && data.errors_csv_b64) {
        // Auto-download the errors CSV so the user can fix + re-upload only the failed rows
        try {
          const bin = atob(data.errors_csv_b64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `bulk-claims-errors-${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a); a.click(); a.remove();
          window.URL.revokeObjectURL(url);
        } catch { /* ignore */ }
        toast.warning(`${data.created_count} claim(s) imported · ${errCount} row(s) failed — check the downloaded errors CSV, fix, and re-upload just those rows.`);
      } else {
        toast.success(`Bulk import: ${data.created_count} claim(s) imported and submitted to your manager — attach receipts to each`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const decide = async (id, status) => {
    try { await api.patch(`/reimbursements/${id}`, { status }); toast.success(`Claim ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { date: "", category: "travel", amount: 0, currency: "INR", description: "" }] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) });
  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, x) => x === i ? { ...l, ...patch } : l) });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Payroll</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Reimbursements</h1>
          <p className="text-sm text-zinc-500 mt-2">Submit multi-line expense claims — travel, fuel, food, hotel and more.</p>
        </div>
        {user?.employee_id && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadTemplate} className="rounded-sm border-zinc-300" data-testid="claim-download-template">
              <DownloadSimple size={14} className="mr-1.5" /> Template
            </Button>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 rounded-sm cursor-pointer" data-testid="claim-bulk-upload">
              <FileXls size={14} /> Bulk upload
              <input type="file" accept=".xlsx" onChange={bulkUpload} className="hidden" />
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-zenith rounded-sm" data-testid="claim-create-btn"><Plus size={16} className="mr-1.5" /> New claim</Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Submit reimbursement claim</DialogTitle></DialogHeader>
              <div className="border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 text-xs text-amber-800 flex items-start gap-1.5">
                <WarningCircle size={14} className="mt-0.5 flex-shrink-0" weight="fill" />
                <span>The claim is <b>submitted to your manager</b> straight away. Attach at least one receipt so it can be approved.</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3 space-y-1"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" placeholder="e.g. Bangalore client visit — March" data-testid="claim-title-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Trip start</Label><Input type="date" value={form.trip_start_date} onChange={(e) => setForm({ ...form, trip_start_date: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Trip end</Label><Input type="date" value={form.trip_end_date} onChange={(e) => setForm({ ...form, trip_end_date: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-3 space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Expense line items</Label>
                  <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm h-7" data-testid="claim-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
                </div>
                <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                  {form.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end border border-zinc-200 rounded-sm p-2">
                      <div className="col-span-2"><Label className="text-[10px]">Date</Label><Input type="date" value={l.date} onChange={(e) => setLine(i, { date: e.target.value })} className="rounded-sm h-8 text-xs" /></div>
                      <div className="col-span-3"><Label className="text-[10px]">Category</Label>
                        <Select value={l.category} onValueChange={(v) => setLine(i, { category: v })}>
                          <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Label className="text-[10px]">Amount</Label><Input type="number" step="0.01" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} className="rounded-sm h-8 text-xs font-mono-plex" data-testid={`claim-amount-${i}`} /></div>
                      <div className="col-span-1"><Label className="text-[10px]">Cur</Label><Input value={l.currency} onChange={(e) => setLine(i, { currency: e.target.value.toUpperCase() })} className="rounded-sm h-8 text-xs font-mono-plex" maxLength={3} /></div>
                      <div className="col-span-3"><Label className="text-[10px]">Description</Label><Input value={l.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} className="rounded-sm h-8 text-xs" /></div>
                      <div className="col-span-1 pb-0.5">{form.lines.length > 1 && <button onClick={() => rmLine(i)} className="text-red-600 hover:opacity-70"><Trash size={14} /></button>}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right text-xs">
                  Total: {Object.entries(total).map(([c, v]) => <span key={c} className="ml-3 font-mono-plex font-semibold">{c} {v.toLocaleString("en-IN")}</span>)}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} className="btn-zenith rounded-sm" data-testid="claim-submit">Submit claim</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {user?.employee_id && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">My claims</h2>
          <ClaimsTable rows={mine} onChanged={load} currentUser={user} onSubmitDraft={submitDraft} />
        </section>
      )}
      {canReview && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">All claims</h2>
          <ClaimsTable rows={all} showEmployee decide={decide} onChanged={load} currentUser={user} />
        </section>
      )}
    </div>
  );
}

function ClaimsTable({ rows, showEmployee, decide, onChanged, currentUser, onSubmitDraft }) {
  const canWriteAtt = (r) => {
    if (!currentUser) return false;
    if (["super_admin", "company_admin", "hr", "finance"].includes(currentUser.role)) return true;
    return r.employee_id === currentUser.employee_id && ["draft", "submitted", "pending", undefined, null].includes(r.status);
  };
  return (
    <div className="overflow-x-auto border border-zinc-200 rounded-sm">
      <table className="hr-table">
        <thead><tr>
          {showEmployee && <th>Employee</th>}
          <th>Title</th><th>Trip</th><th>Lines</th><th>Total</th><th>Files</th><th>Status</th>{decide && <th>Actions</th>}{!decide && onSubmitDraft && <th></th>}
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={showEmployee ? 8 : 7} className="text-center py-6 text-zinc-500 text-sm">No claims.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              {showEmployee && <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>}
              <td>{r.title}{r.source === "bulk_upload" && <span className="ml-1 text-[9px] px-1 py-0.5 border border-zinc-300 rounded-sm bg-zinc-50">bulk</span>}{r.source === "trip" && <span className="ml-1 text-[9px] px-1 py-0.5 border border-emerald-300 text-emerald-700 rounded-sm bg-emerald-50">trip</span>}</td>
              <td className="font-mono-plex text-xs">{r.trip_start_date || "—"} → {r.trip_end_date || "—"}</td>
              <td className="font-mono-plex text-xs">{(r.lines || []).length}</td>
              <td className="font-mono-plex text-xs font-semibold">
                {Object.entries(r.total_by_currency || {}).map(([c, v]) => <div key={c}>{fmtMoney(v, c)}</div>)}
              </td>
              <td><AttachmentsDialog claim={r} onChanged={onChanged || (() => {})} canWrite={canWriteAtt(r)} /></td>
              <td><Badge variant="outline" className={`rounded-sm ${STATUS_STYLE[r.status] || ""}`}>{r.status || "draft"}</Badge></td>
              {decide && (
                <td>
                  {r.status === "submitted" && (
                    <div className="flex gap-1">
                      <button onClick={() => decide(r.id, "approved")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`claim-approve-${r.id}`}><ThumbsUp size={12} className="inline mr-1" /> Approve</button>
                      <button onClick={() => decide(r.id, "rejected")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm"><ThumbsDown size={12} className="inline mr-1" /> Reject</button>
                    </div>
                  )}
                  {r.status === "approved" && <button onClick={() => decide(r.id, "paid")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm">Mark paid</button>}
                </td>
              )}
              {!decide && onSubmitDraft && (
                <td>
                  {r.status === "draft" && (
                    <button
                      onClick={() => onSubmitDraft(r.id)}
                      disabled={!(r.attachments || []).length}
                      title={!(r.attachments || []).length ? "Attach at least one receipt first" : "Submit for approval"}
                      className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`claim-submit-draft-${r.id}`}
                    >
                      <PaperPlaneTilt size={12} weight="fill" /> Submit
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
